import React from 'react'
import { View, Dimensions, TouchableOpacity } from 'react-native'
import {
  TabBar,
  TabView as NativeTabView,
  TabBarProps,
  TabViewProps as TabViewNativeProps,
  Route,
  SceneRendererProps,
  NavigationState,
  TabDescriptor,
  TabBarItemProps,
} from 'react-native-tab-view'
import { useTheme } from '@ui-kitten/components'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Text } from '@components/base'

interface TabRoute extends Route {
  sceneComponent: React.ReactNode
}

interface RenderTabBarParams extends SceneRendererProps {
  navigationState: NavigationState<TabRoute>
}

export interface TabViewProps extends Omit<Partial<TabViewNativeProps<TabRoute>>, 'tabIndex'> {
  tabIndex: number
  onChangeTabIndex: (tabIndex: number) => void
  tabBarProps?: Partial<TabBarProps<TabRoute>>
  routes: TabRoute[]
  staticHeader?: React.ReactNode
  variant?: 'default' | 'small' | 'pill'
}

const renderScene = ({ route }: { route: TabRoute }) => route.sceneComponent

const getOptions = (routes: TabRoute[]) => {
  return routes.reduce((acc, route) => {
    acc[route.key] = {
      label: ({ route, color }) => (
        <Text type="large" style={{ color }} numberOfLines={1}>
          {route.title}
        </Text>
      ),
    }
    return acc
  }, {} as Record<string, TabDescriptor<TabRoute>>)
}

const initialLayout = { width: Dimensions.get('window').width }

export const TabView = ({
  tabIndex,
  onChangeTabIndex,
  tabBarProps,
  routes,
  staticHeader,
  variant = 'default',
  ...restProps
}: TabViewProps) => {
  const styles = useStyleSheet(themedStyles)
  const theme = useTheme()
  const isPillVariant = variant === 'pill'
  const isSmallVariant = variant === 'small'

  const renderTabBarPill = ({ route, navigationState }: TabBarItemProps<TabRoute>) => {
    const isFocused = navigationState.index === navigationState.routes.indexOf(route)

    return (
      <TouchableOpacity
        accessibilityLabel={route.title}
        style={[styles.pill, isFocused ? styles.activePillColor : styles.defaultPillColor]}
        onPress={() => onChangeTabIndex(routes.findIndex((r) => r.key === route.key))}
      >
        <Text type="regular" style={[isFocused && styles.activePillLabel]}>
          {route.title}
        </Text>
      </TouchableOpacity>
    )
  }

  const renderTabBar = (props: RenderTabBarParams) => (
    <View>
      <TabBar
        {...props}
        {...tabBarProps}
        options={getOptions(routes)}
        style={[styles.tabBar, isPillVariant && styles.pillTab]}
        tabStyle={styles.tab}
        indicatorStyle={[
          isPillVariant ? styles.pillTabIndicator : styles.tabIndicator,
          isSmallVariant && styles.smallIndicator,
        ]}
        contentContainerStyle={isPillVariant ? styles.pillContentContainer : undefined}
        activeColor={theme['theme.text.primary']}
        inactiveColor={theme['theme.text.tertiary']}
        renderTabBarItem={isPillVariant ? renderTabBarPill : undefined}
      />
      {staticHeader}
    </View>
  )

  return (
    <View style={styles.container}>
      <NativeTabView
        style={styles.container}
        navigationState={{ index: tabIndex, routes }}
        onIndexChange={onChangeTabIndex}
        renderScene={renderScene}
        renderTabBar={renderTabBar}
        initialLayout={initialLayout}
        {...restProps}
      />
    </View>
  )
}

const themedStyles = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  tabBar: {
    justifyContent: 'center',
    height: 48,
    backgroundColor: 'theme.background',
    boxShadow: 'none',
  },
  tab: {
    padding: 0,
  },
  tabIndicator: {
    height: 4,
    borderRadius: 2,
    backgroundColor: 'theme.text.primary',
  },
  smallIndicator: {
    width: 24,
    marginHorizontal: 86,
  },
  pillContentContainer: {
    paddingHorizontal: 12,
  },
  pillTab: {
    elevation: 0,
    shadowOpacity: 0,
    borderBottomWidth: 0,
  },
  pillTabIndicator: {
    height: 0,
  },
  pill: {
    height: 34,
    borderRadius: 32,
    paddingHorizontal: 16,
    marginHorizontal: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  defaultPillColor: {
    backgroundColor: 'theme.surface.base2',
  },
  activePillColor: {
    backgroundColor: 'theme.primary.base',
  },
  activePillLabel: {
    color: 'theme.solid.white',
  },
})
